import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Phone, Mail, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "hospitality", label: "Hospitality" },
  { value: "bonsai", label: "Bonsai Acquisition" },
  { value: "maintenance", label: "Plant Maintenance" },
  { value: "other", label: "Other" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_type: form.project_type || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you. We will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or write to sujal@indoorgreens.in";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F2EFEB] py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left: information */}
          <div className="lg:col-span-5 reveal">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-6">
              10 / Begin a conversation
            </p>
            <h2 className="font-[Outfit] font-extralight text-[#2D332B] text-[36px] sm:text-[56px] leading-[1.02] tracking-[-0.015em]">
              Let&rsquo;s shape a
              <br />
              <span className="italic text-[#8A9A86]">living space</span>{" "}
              together.
            </h2>

            <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#5C6658]">
              Whether it is a private residence, a hotel lobby or a single
              bonsai for the desk you love, we would be glad to listen.
            </p>

            <div className="mt-12 space-y-7">
              <a
                href="tel:+917738035670"
                data-testid="contact-phone-link"
                className="flex items-start gap-5 group"
              >
                <span className="flex items-center justify-center w-10 h-10 border border-[#E5E1DB] bg-[#F9F8F6] rounded-sm">
                  <Phone className="w-4 h-4 text-[#8A9A86]" />
                </span>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                    Phone
                  </p>
                  <p className="mt-1 font-[Outfit] text-[20px] font-light text-[#2D332B] group-hover:text-[#8A9A86] transition-colors">
                    +91 77380 35670
                  </p>
                </div>
              </a>

              <a
                href="mailto:sujal@indoorgreens.in"
                data-testid="contact-email-link"
                className="flex items-start gap-5 group"
              >
                <span className="flex items-center justify-center w-10 h-10 border border-[#E5E1DB] bg-[#F9F8F6] rounded-sm">
                  <Mail className="w-4 h-4 text-[#8A9A86]" />
                </span>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                    Email
                  </p>
                  <p className="mt-1 font-[Outfit] text-[20px] font-light text-[#2D332B] group-hover:text-[#8A9A86] transition-colors">
                    sujal@indoorgreens.in
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-5" data-testid="contact-location">
                <span className="flex items-center justify-center w-10 h-10 border border-[#E5E1DB] bg-[#F9F8F6] rounded-sm">
                  <MapPin className="w-4 h-4 text-[#8A9A86]" />
                </span>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                    Studio
                  </p>
                  <p className="mt-1 font-[Outfit] text-[20px] font-light text-[#2D332B]">
                    Mumbai, India
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#F9F8F6] border border-[#E5E1DB] p-8 sm:p-12 rounded-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]"
                  >
                    Full name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus-visible:ring-0 focus-visible:border-[#8A9A86] px-0 h-11 text-[#2D332B] placeholder:text-[#A6AAA0]"
                    required
                  />
                </div>

                <div>
                  <Label
                    htmlFor="email"
                    className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]"
                  >
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus-visible:ring-0 focus-visible:border-[#8A9A86] px-0 h-11 text-[#2D332B] placeholder:text-[#A6AAA0]"
                    required
                  />
                </div>

                <div>
                  <Label
                    htmlFor="phone"
                    className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]"
                  >
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Optional"
                    className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus-visible:ring-0 focus-visible:border-[#8A9A86] px-0 h-11 text-[#2D332B] placeholder:text-[#A6AAA0]"
                  />
                </div>

                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                    Project type
                  </Label>
                  <Select
                    value={form.project_type}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, project_type: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="contact-input-project-type"
                      className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus:ring-0 focus-visible:ring-0 focus:border-[#8A9A86] px-0 h-11 text-[#2D332B]"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((p) => (
                        <SelectItem
                          key={p.value}
                          value={p.value}
                          data-testid={`project-type-${p.value}`}
                        >
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-8">
                <Label
                  htmlFor="message"
                  className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]"
                >
                  Tell us about your space *
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Square footage, light conditions, mood references, timing…"
                  className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus-visible:ring-0 focus-visible:border-[#8A9A86] px-0 resize-none text-[#2D332B] placeholder:text-[#A6AAA0]"
                  required
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <p className="text-[11px] text-[#5C6658] leading-relaxed max-w-sm">
                  By submitting, you agree to be contacted regarding your
                  enquiry. We never share your details.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center px-8 py-3.5 bg-[#2D332B] text-[#F9F8F6] text-[11px] tracking-[0.3em] uppercase hover:bg-[#8A9A86] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-500 rounded-sm"
                >
                  {submitting ? "Sending…" : "Send enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
